import { useEffect, useState } from "react";
import "assets/css/notification.css";
import {
  SecondaryHeading,
  BodyContainer,
  RightMainContainer,
  HeaderContainer,
  PrimaryHeading,
  MainButton,
  Loader,
  CheckBox,
  Divider,
} from "commonUI";
import { SaveIcon } from "allIcons";
import { defaultNotificationSetting } from "./enum";
import {
  useNotifications,
  useUpdateNotificationSetting,
} from "api/siteSettings/notifications";
import { isEqual, parse, useSearch } from "helpers";
import { useUserDetails, useUserPermissions } from "hooks/user";
import { t } from "i18next";

export const NotificationList = ({
  email,
  web,
  setEmail,
  setWeb,
  title,
  last,
}) => {
  return (
    <>
      <div className={`notification-list-card ${last ? "border-0" : ""}`}>
        <div className="flex items-center">
          <div className="w-full self-center">
            <SecondaryHeading className="text-13 font-semibold text-theme-secondary">
              {title ?? ""}
            </SecondaryHeading>
          </div>
          <div className="flex items-center w-40 justify-between">
            <div className="">
              <CheckBox value={email} setValue={setEmail} />
            </div>
            <div className="">
              <CheckBox value={web} setValue={setWeb} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default function Notifications() {
  const { web = null } = useSearch();
  const [notificationSetting, setNotificationSetting] = useState(
    defaultNotificationSetting
  );
  const { add_new_items_to_site_template, edit_item_details_in_site_template } =
    useUserPermissions();
  const handleChangeNotificationSetting = (key, value) => {
    setNotificationSetting((prev) => ({
      ...prev,
      [key]: value,
    }));
  };
  const { data: notifications, isLoading: loadingNotification } =
    useNotifications();
  const { isLoading } = useUserDetails();
  const {
    mutate: updateNotificationSetting,
    isLoading: updatingNotificationSetting,
  } = useUpdateNotificationSetting();

  useEffect(() => {
    if (!loadingNotification) {
      let notificationData = notifications?.find((_) =>
        isEqual(_.website_id, web)
      );
      if (notificationData) {
        setNotificationSetting(
          parse(
            notificationData?.noti_settings ??
              notificationData?.notifications_settings
          )
        );
      }
    }
  }, [notifications, loadingNotification, web]);

  const handleSaveChanges = () => {
    let notificationData = notifications?.find((_) =>
      isEqual(_.website_id, web)
    );
    updateNotificationSetting({
      api_key: notificationData?.apiKey || localStorage.getItem("apikey"),
      added_by: notificationData?.added_by || localStorage.getItem("user_id"),
      website_id: notificationData?.website_id || web,
      system_status: notificationData?.system_status || 0,
      notifications_settings: notificationSetting,
    });
  };

  return (
    <>
      <RightMainContainer>
        <Loader
          show={
            updatingNotificationSetting || (loadingNotification && !isLoading)
          }
          loader="block"
        />
        <HeaderContainer>
          <PrimaryHeading>{t("siteSetting.notifications.title")}</PrimaryHeading>
          {add_new_items_to_site_template ||
          edit_item_details_in_site_template ? (
            <MainButton
              disabled={loadingNotification || updatingNotificationSetting}
              onClick={handleSaveChanges}
            >
              <SaveIcon />
              {t("button.saveChanges")}
            </MainButton>
          ) : (
            ""
          )}
        </HeaderContainer>
        <BodyContainer>
          <div className="flex items-center justify-between mb-3">
            <PrimaryHeading className="text-17">
              {t("siteSetting.notifications.anActionIsCompleted")}
            </PrimaryHeading>
            <div>
              <div className="notification-detail-header">
                <h2 className="text-theme-secondary font-bold">{t("siteSetting.notifications.notificationType.email")}</h2>
                <h2 className="text-theme-secondary font-bold">{t("siteSetting.notifications.notificationType.website")}</h2>
              </div>
            </div>
          </div>

          <NotificationList
            title={t("siteSetting.notifications.anActionIsCompleted.anAssignedUserViewAnItemForTheFirstTime")}
            last={true}
            email={
              notificationSetting.an_assigned_user_views_an_item_for_the_first_time_email
            }
            web={
              notificationSetting.an_assigned_user_views_an_item_for_the_first_time_website
            }
            setEmail={(value) =>
              handleChangeNotificationSetting(
                "an_assigned_user_views_an_item_for_the_first_time_email",
                value
              )
            }
            setWeb={(value) =>
              handleChangeNotificationSetting(
                "an_assigned_user_views_an_item_for_the_first_time_website",
                value
              )
            }
          />
          <Divider className="mb-3 mt-3" height={2}/>
          <div className="mb-2 mt-2">
            <PrimaryHeading className="text-17">
              {t("siteSetting.notifications.activityInADiscussion")}
            </PrimaryHeading>
          </div>

          <NotificationList
            title={t("siteSetting.notifications.activityInADiscussion.userIsMentioned")}
            email={notificationSetting["a_user_is_@Mentioned_email"]}
            web={notificationSetting["a_user_is_@Mentioned_website"]}
            setEmail={(value) =>
              handleChangeNotificationSetting(
                "a_user_is_@Mentioned_email",
                value
              )
            }
            setWeb={(value) =>
              handleChangeNotificationSetting(
                "a_user_is_@Mentioned_website",
                value
              )
            }
          />
          <NotificationList
            title={t("siteSetting.notifications.activityInADiscussion.replyIsAdded")}
            email={notificationSetting["a_reply_is_added_email"]}
            web={notificationSetting["a_reply_is_added_website"]}
            setEmail={(value) =>
              handleChangeNotificationSetting("a_reply_is_added_email", value)
            }
            setWeb={(value) =>
              handleChangeNotificationSetting("a_reply_is_added_website", value)
            }
          />
          <NotificationList
            title={t("siteSetting.notifications.activityInADiscussion.itemIsAdded")}
            last={true}
            email={notificationSetting["an_item_is_added_email"]}
            web={notificationSetting["an_item_is_added_website"]}
            setEmail={(value) =>
              handleChangeNotificationSetting("an_item_is_added_email", value)
            }
            setWeb={(value) =>
              handleChangeNotificationSetting("an_item_is_added_website", value)
            }
          />
          <Divider className="mb-3 mt-3" height={2}/>
          <div className="flex items-center justify-between mb-2 mt-2">
            <PrimaryHeading className="text-17">
              {t("siteSetting.notifications.changesToItemDetails")}
            </PrimaryHeading>
            {/* <div>
              <div className="notification-detail-header">
                <h2 className="text-theme-secondary font-bold">Email</h2>
                <h2 className="text-theme-secondary font-bold">Website</h2>
              </div>
            </div>
            */}
          </div>
          {/* <div className="mb-2">
            <PrimaryHeading className="text-17">
              Changes To Item Details
            </PrimaryHeading>
          </div>
          <hr className="line mb-0" /> */}
          <NotificationList
            title={t("siteSetting.notifications.changesToItemDetails.titleIsUpdated")}
            email={notificationSetting["the_title_is_updated_email"]}
            web={notificationSetting["the_title_is_updated_website"]}
            setEmail={(value) =>
              handleChangeNotificationSetting(
                "the_title_is_updated_email",
                value
              )
            }
            setWeb={(value) =>
              handleChangeNotificationSetting(
                "the_title_is_updated_website",
                value
              )
            }
          />
          <NotificationList
            title={t("siteSetting.notifications.changesToItemDetails.userIsAssigned")}
            email={notificationSetting["a_user_is_assigned_email"]}
            web={notificationSetting["a_user_is_assigned_website"]}
            setEmail={(value) =>
              handleChangeNotificationSetting("a_user_is_assigned_email", value)
            }
            setWeb={(value) =>
              handleChangeNotificationSetting(
                "a_user_is_assigned_website",
                value
              )
            }
          />
          {/* <NotificationList
            title="A User Is Unassigned"
            email={notificationSetting["a_user_is_unassigned_email"]}
            web={notificationSetting["a_user_is_unassigned_website"]}
            setEmail={(value) =>
              handleChangeNotificationSetting(
                "a_user_is_unassigned_email",
                value
              )
            }
            setWeb={(value) =>
              handleChangeNotificationSetting(
                "a_user_is_unassigned_website",
                value
              )
            }
          /> */}
          <NotificationList
            title={t("siteSetting.notifications.changesToItemDetails.statusIsUpdated")}
            email={notificationSetting["the_status_is_updated_email"]}
            web={notificationSetting["the_status_is_updated_website"]}
            setEmail={(value) =>
              handleChangeNotificationSetting(
                "the_status_is_updated_email",
                value
              )
            }
            setWeb={(value) =>
              handleChangeNotificationSetting(
                "the_status_is_updated_website",
                value
              )
            }
          />
          <NotificationList
            title={t("The Action is completed")}
            email={notificationSetting["an_action_is_completed_email"]}
            web={notificationSetting["an_action_is_completed_website"]}
            setEmail={(value) =>
              handleChangeNotificationSetting(
                "an_action_is_completed_email",
                value
              )
            }
            setWeb={(value) =>
              handleChangeNotificationSetting(
                "an_action_is_completed_website",
                value
              )
            }
          />
          <NotificationList
            title={t("siteSetting.notifications.changesToItemDetails.priorityUpdated")}
            email={notificationSetting["the_priority_is_updated_email"]}
            web={notificationSetting["the_priority_is_updated_website"]}
            setEmail={(value) =>
              handleChangeNotificationSetting(
                "the_priority_is_updated_email",
                value
              )
            }
            setWeb={(value) =>
              handleChangeNotificationSetting(
                "the_priority_is_updated_website",
                value
              )
            }
          />
          <NotificationList
            title={t("siteSetting.notifications.changesToItemDetails.dueDateUpdated")}
            last={true}
            email={notificationSetting["the_due_date_is_updated_email"]}
            web={notificationSetting["the_due_date_is_updated_website"]}
            setEmail={(value) =>
              handleChangeNotificationSetting(
                "the_due_date_is_updated_email",
                value
              )
            }
            setWeb={(value) =>
              handleChangeNotificationSetting(
                "the_due_date_is_updated_website",
                value
              )
            }
          />
          {/* <NotificationList
            title="The Tags Is Updated"
            last={true}
            web={notificationSetting["the_tags_is_updated_website"]}
            email={notificationSetting["the_tags_is_updated_email"]}
            setEmail={(value) =>
              handleChangeNotificationSetting(
                "the_tags_is_updated_email",
                value
              )
            }
            setWeb={(value) =>
              handleChangeNotificationSetting(
                "the_tags_is_updated_website",
                value
              )
            }
          /> */}
          <Divider className="mb-3 mt-3" height={2}/>
          <div className="mb-2 mt-2">
            <PrimaryHeading className="text-17">
              {t("siteSetting.notifications.assignmentReminders")}
            </PrimaryHeading>
          </div>
          <NotificationList
            title={t("siteSetting.notifications.assignmentReminders.7daysBefore")}
            email={notificationSetting["7_days_before_due_date_email"]}
            web={notificationSetting["7_days_before_due_date_website"]}
            setEmail={(value) =>
              handleChangeNotificationSetting(
                "7_days_before_due_date_email",
                value
              )
            }
            setWeb={(value) =>
              handleChangeNotificationSetting(
                "7_days_before_due_date_website",
                value
              )
            }
          />
          <NotificationList
            title={t("siteSetting.notifications.assignmentReminders.3daysBefore")}
            email={notificationSetting["3_days_before_due_date_email"]}
            web={notificationSetting["3_days_before_due_date_website"]}
            setEmail={(value) =>
              handleChangeNotificationSetting(
                "3_days_before_due_date_email",
                value
              )
            }
            setWeb={(value) =>
              handleChangeNotificationSetting(
                "3_days_before_due_date_website",
                value
              )
            }
          />
          <NotificationList
            title={t("siteSetting.notifications.assignmentReminders.1dayBefore")}
            email={notificationSetting["1_day_before_due_date_email"]}
            web={notificationSetting["1_day_before_due_date_website"]}
            setEmail={(value) =>
              handleChangeNotificationSetting(
                "1_day_before_due_date_email",
                value
              )
            }
            setWeb={(value) =>
              handleChangeNotificationSetting(
                "1_day_before_due_date_website",
                value
              )
            }
          />
          <NotificationList
            title={t("siteSetting.notifications.assignmentReminders.onDueDate")}
            email={notificationSetting["on_due_date_email"]}
            web={notificationSetting["on_due_date_website"]}
            setEmail={(value) =>
              handleChangeNotificationSetting("on_due_date_email", value)
            }
            setWeb={(value) =>
              handleChangeNotificationSetting("on_due_date_website", value)
            }
          />
          <NotificationList
            title={t("siteSetting.notifications.assignmentReminders.everyDayAfterDueDate")}
            last={true}
            email={notificationSetting["every_day_after_due_date_email"]}
            web={notificationSetting["every_day_after_due_date_website"]}
            setEmail={(value) =>
              handleChangeNotificationSetting(
                "every_day_after_due_date_email",
                value
              )
            }
            setWeb={(value) =>
              handleChangeNotificationSetting(
                "every_day_after_due_date_website",
                value
              )
            }
          />
        </BodyContainer>
      </RightMainContainer>
    </>
  );
}
